import { showToast } from '@lokalise/louis';

import completeDownloadOnboarding from '@app/features/quick-start/utils/completeDownloadOnboarding';
import useDispatch from '@app/hooks/useDispatch';
import useSelector from '@app/hooks/useSelector';

import { getProjectBranchName, getProjectId } from '../../reducers/downloadData/selectors';
import { downloadExportActions } from '../../reducers/downloadExport/reducers';
import { getDownloadExportError } from '../../reducers/downloadExport/selectors';
import { downloadGenerateActions } from '../../reducers/downloadGenerate/reducers';
import { getDownloadGenerateFile } from '../../reducers/downloadGenerate/selectors';
import type { ExportConfig } from '../../types';
import ExportButtons from './ExportButtons';
import type ExportButtonsMode from './types';

interface ExportButtonsContainerProps {
    values: ExportConfig;
    mode: ExportButtonsMode;
    gitIntegrationEnabled: boolean;
}

const ExportButtonsContainer = ({ values, mode, gitIntegrationEnabled }: ExportButtonsContainerProps) => {
    const dispatch = useDispatch();

    const file = useSelector(getDownloadGenerateFile);
    const error = useSelector(getDownloadExportError);
    const projectId = useSelector(getProjectId);
    const projectBranchName = useSelector(getProjectBranchName);

    if (projectId === undefined || projectBranchName === undefined) {
        return null;
    }

    const data = { values, projectBranchName, projectId };

    const isEmptyLanguages = () => values?.exportLangs?.length === 0;

    const showEmptyLanguagesErrorToast = () => {
        showToast({
            type: 'error',
            title: 'Something went wrong!',
            description: 'Select at least one language.',
        });
    };

    const onPreviewClick = () => {
        if (isEmptyLanguages()) {
            showEmptyLanguagesErrorToast();

            return;
        }
        dispatch(downloadExportActions.preview({ ...data, isPreview: true }));
    };

    const onGenerateClick = () => {
        if (isEmptyLanguages()) {
            showEmptyLanguagesErrorToast();

            return;
        }
        dispatch(downloadExportActions.generate(data));
    };

    const onDownloadClick = () => {
        if (isEmptyLanguages()) {
            showEmptyLanguagesErrorToast();

            return;
        }
        dispatch(downloadExportActions.download(data));
    };

    const onDownloadAfterGenerateClick = () => {
        if (isEmptyLanguages()) {
            showEmptyLanguagesErrorToast();

            return;
        }
        dispatch(downloadExportActions.downloadAfterGenerate(data));
    };

    return (
        <ExportButtons
            mode={mode}
            fileFormat={values.fileFormat}
            file={file}
            error={error}
            gitIntegrationEnabled={gitIntegrationEnabled}
            onCloseGeneratedFileModal={() => {
                dispatch(downloadGenerateActions.setFile(null));
                completeDownloadOnboarding(false);
            }}
            onCloseErrorModal={() => dispatch(downloadExportActions.setError(null))}
            onDownload={onDownloadClick}
            onDownloadHandler={onDownloadAfterGenerateClick}
            onGenerate={onGenerateClick}
            onPreview={onPreviewClick}
        />
    );
};

export default ExportButtonsContainer;
